import React, { useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { useGSAP } from '@gsap/react';
import Button from '../components/Button';
import VectorLoader from 'components/VectorLoader';
import StepIndicator from 'components/StepIndicator';
import { useTheme } from 'hooks/useTheme';
import useMediaQuery from 'hooks/useMediaQuery';
import ThemeToggle from '../components/ThemeToggle';
import { renderWithLineBreaks } from 'utils/renderWithLineBreaks';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const contentYOffset = 90;

const HomeFPCarousel = ({ className = '', panelData }) => {
  const navigate = useNavigate();
  const isMobileSize = useMediaQuery('(max-width: 768px)');
  const { isDarkMode } = useTheme();
  const [activeIndex, setActiveIndex] = useState(0);
  const mainRef = useRef(null);
  const scrollTriggerInstanceRef = useRef(null);
  const panelsRef = useRef([]);

  useLayoutEffect(() => {
    ScrollTrigger.normalizeScroll(true);
    return () => {
      ScrollTrigger.normalizeScroll(false);
    };
  }, []);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          isMobile: '(max-width: 768px)',
          isDesktop: '(min-width: 769px)',
        },
        (context) => {
          const { isMobile } = context.conditions;

          panelsRef.current = gsap.utils.toArray(
            '.fp-carousel-item',
            mainRef.current,
          );
          if (panelsRef.current.length === 0) return;

          const panels = panelsRef.current;
          const titleWrappers = gsap.utils.toArray(
            '.title-wrapper',
            mainRef.current,
          );
          const titles = gsap.utils.toArray('.title-text', mainRef.current);
          const contentDetails = gsap.utils.toArray(
            '.content-detail',
            mainRef.current,
          );
          gsap.set(panels[0], { zIndex: 0, clipPath: 'inset(0% 0% 0% 0%)' });
          gsap.set(titleWrappers[0], { clipPath: 'inset(0% 0% 0% 0%)' });
          gsap.set(contentDetails[0], { y: 0, opacity: 1 });

          panels.forEach((_, index) => {
            if (index > 0) {
              gsap.set(panels[index], {
                zIndex: index,
                clipPath: 'inset(100% 0% 0% 0%)',
              });
              gsap.set(titleWrappers[index], {
                clipPath: isMobile
                  ? 'inset(0% 0% 100% 0%)'
                  : 'inset(100% 0% 0% 0%)',
              });
              gsap.set(contentDetails[index], {
                y: contentYOffset,
                opacity: 0,
              });
            }
          });

          gsap.set(titles[0], { y: 0, opacity: 1 });
          titles.forEach((title, index) => {
            if (index > 0) {
              const yOffset = titleWrappers[index].offsetHeight;
              gsap.set(title, {
                y: isMobile ? -yOffset : yOffset,
                opacity: 0,
              });
            }
          });

          const tl = gsap.timeline();
          const N = panels.length;

          for (let i = 0; i < N - 1; i++) {
            const yOffset = titleWrappers[i + 1].offsetHeight;
            tl.to(
              panels[i + 1],
              { clipPath: 'inset(0% 0% 0% 0%)', duration: 1 },
              i,
            )
              .fromTo(
                titleWrappers[i + 1],
                {
                  clipPath: isMobile
                    ? 'inset(0% 0% 100% 0%)'
                    : 'inset(100% 0% 0% 0%)',
                },
                { clipPath: 'inset(0% 0% 0% 0%)', duration: 1 },
                i,
              )

              .fromTo(
                titles[i + 1],
                { y: isMobile ? -yOffset : yOffset, opacity: 0 },
                { y: 0, opacity: 1, duration: 1 },
                i,
              )
              .fromTo(
                contentDetails[i + 1],
                { y: contentYOffset, opacity: 0 },
                { y: 0, opacity: 1, duration: 1 },
                i,
              )
              .to(titles[i], { opacity: 0, duration: 1 }, i)
              .to(contentDetails[i], { opacity: 0, duration: 1 }, i);
          }

          scrollTriggerInstanceRef.current = ScrollTrigger.create({
            animation: tl,
            trigger: mainRef.current,
            pin: mainRef.current,
            scrub: 0.5,
            start: 'top top',
            end: () => `+=${(N - 1) * mainRef.current.offsetHeight}`,
            onUpdate: (self) =>
              setActiveIndex(Math.round(self.progress * (N - 1))),
            invalidateOnRefresh: true,
          });
        },
      );

      return () => {
        mm.revert();
      };
    },
    {
      scope: mainRef,
      dependencies: [panelData],
      revertOnUpdate: true,
    },
  );

  const handleStepClick = (index) => {
    const st = scrollTriggerInstanceRef.current;
    if (!st || index === activeIndex) return;
    const offset = index === 0 ? 0 : 1;
    const targetScroll =
      st.start + index * mainRef.current.offsetHeight - offset;
    gsap.to(window, {
      scrollTo: {
        y: targetScroll,
        autoKill: false,
      },
      duration: 1.2,
      ease: 'power2.inOut',
      overwrite: true,
    });
  };

  return (
    <div
      ref={mainRef}
      className={`home-fp-carousel full-pager relative ${className}`}
    >
      {panelData.slides.map((panel, index) => (
        <div
          key={index + panel.title}
          className='fp-carousel-item overflow-hidden full-height full-width page-wrapper absolute image-styles will-change-cp'
          style={{
            backgroundImage: `url(${
              isDarkMode
                ? panel.darkModeImageUrl || panel.lightModeImageUrl
                : panel.lightModeImageUrl
            })`,
          }}
        >
          <div
            className={`title-wrapper ${
              isMobileSize ? '' : 'absolute'
            } full-width overflow-hidden will-change-cp`}
          >
            <p className='title-text uppercase full-width sec-nd-hp-p font-primary font-white-color balanced-text'>
              {panel.title && (
                <>
                  {renderWithLineBreaks(panel.title)} <br />
                </>
              )}
              {panel.opacityTitle && (
                <>
                  <span className='font-white-color opacity-04'>
                    {renderWithLineBreaks(panel.opacityTitle)}
                  </span>
                  <br />
                </>
              )}
              {panel.italicTitle && (
                <span className='font-secondary font-white-color opacity-04'>
                  {renderWithLineBreaks(panel.italicTitle)}
                </span>
              )}
            </p>
          </div>
          <div
            className={`content-detail ${
              isMobileSize ? '' : 'absolute'
            } flex flex-column row-gap-40 will-change-transform-opacity`}
          >
            {panel.description && (
              <p className='paragraph-font-grotesk font-white-color'>
                {panel.description}
              </p>
            )}
            {panel.buttonLink && panel.buttonText && (
              <Button onClick={() => navigate(panel.buttonLink)}>
                {panel.buttonText}
              </Button>
            )}
          </div>
          {!isMobileSize && (
            <ThemeToggle className='absolute bottom-0 left-0 page-wrapper' />
          )}
        </div>
      ))}
      <div className='vector-loader-container'>
        <VectorLoader progress={100} />
      </div>
      <div className='home-step-indicator absolute'>
        <StepIndicator
          currentIndex={activeIndex}
          totalSteps={panelData.slides.length}
          onStepClick={handleStepClick}
        />
      </div>
    </div>
  );
};

export default HomeFPCarousel;
